import { Component, Prop, Vue } from "vue-property-decorator";
import { Module, TTMJ } from "@/store/enums.ts";
import { CreateElement } from "vue";
import Card from "@/components/Card.tsx";
import AudioHandler from "./Recorder";
import NotificationBar from "@/components/Notification";
import { fetchAllItemsURL, submitFiles } from "./StorageHandler";
import firebase from "firebase/app";
import UUID from "uuid/v1";
import { taskCompleted } from "./utils";

enum STORY_RETELL_STATE {
  DESCRIPTION,
  LIST
}
@Component
export default class TalkToMeJrStoryRetell extends Vue {
  private _frame: STORY_RETELL_STATE;
  private _onItem: number = NaN;
  private _data: { questionAudio: string; answer?: string }[];
  private _doneList: boolean = false;
  private _disableRecording: boolean = true;
  private _timeout: number = 3000;
  private _enableNext: boolean = false;
  private _enableDescribeNext: boolean = false;
  private audioLoc =
    "/res/protected/t2mejr_instructions/ttmj_retell_stories_autoplay.mp3";
  private supposedCurrentTime: any;

  async changeRecordingToNext(audio: any, duration: number, blob?: any) {
    this._data[this._onItem].answer = blob;

    this._disableRecording = true;
    this._enableNext = false;

    if (this._onItem + 1 === this._data.length) {
      this._enableNext = true;
      this._doneList = true;
      await this._submitData();
      this.$forceUpdate();
    } else {
      ++this._onItem;
    }

    this.$forceUpdate();
    let a: any = this.$refs.audio;
    a.load();
  }

  private async _submitData() {
    let currentUser = firebase.auth().currentUser;

    if (!currentUser) return;

    let dir: string = "users/" + currentUser.uid + "/t2mejr/story_retell/";
    let i = 0;
    let uid = UUID();

    this.$store.dispatch("addNotification", {
      type: "info",
      value: "Uploading your answers please wait",
      timeout: -1,
      icon: "mdi-microphone",
      uid: uid
    });

    for (const item of this._data) {
      await submitFiles(
        dir + "story_retell" + ++i + ".webm",
        item.answer,
        (snapshot: any) => {
          let progress =
            ((snapshot.bytesTransferred / snapshot.totalBytes) * 100 * i) /
            this._data.length;
          this.$store.dispatch("updateVisibleNotification", {
            value:
              progress < 99
                ? "Please wait while we upload your answer"
                : "Successfully updated",
            type: progress < 99 ? "info" : "success",
            uid: uid,
            loaderValue: progress,
            timeout: progress < 99 ? -1 : 4000
          });

          // set task completed in firestore
          if (snapshot.bytesTransferred == snapshot.totalBytes) {
            if (!currentUser) return;
            taskCompleted(currentUser.uid, TTMJ.STORY);
          }
        }
      );
    }
  }

  constructor() {
    super();
    this._frame = STORY_RETELL_STATE.DESCRIPTION;
    this._data = [];
  }

  created() {
    this._enableDescribeNext = false;
    this.$store.dispatch("setClip", {
      path: this.audioLoc,
      callbackFunction: () => {
        this._enableDescribeNext = true;
        this.$forceUpdate();
      }
    });
    this.$forceUpdate();

    this._getData();
    this._frame = STORY_RETELL_STATE.DESCRIPTION;
    this._disableRecording = true;
  }

  beforeDestroy() {
    this.$store.dispatch("removeClip", this.audioLoc);
  }

  private _enableRecording() {
    (this.$refs.audio as any).src = "";
    this._disableRecording = false;
    this.$forceUpdate();
  }

  updated() {
    let a: any = this.$refs.audio;
    if (a) {
      a.addEventListener("ended", this._enableRecording);
      a.addEventListener("timeupdate", () => {
        if (!a.seeking) {
          this.supposedCurrentTime = a.currentTime;
        }
      });
      // prevent user from seeking
      a.addEventListener("seeking", () => {
        // guard agains infinite recursion:
        // user seeks, seeking is fired, currentTime is modified, seeking is fired, current time is modified, ....
        var delta = a.currentTime - this.supposedCurrentTime;
        if (Math.abs(delta) > 0.01) {
          a.currentTime = this.supposedCurrentTime;
        }
      });
    }
  }

  private async _getData() {
    this._onItem = 0;
    this._data = [];
    let data: any = await fetchAllItemsURL("res/protected/story_retell");
    data.map((i: any) => this._data.push({ questionAudio: i }));
  }

  render(h: CreateElement) {
    return (
      <span>
        {this._description()}
        {this._words()}
        {this._doneList && (
          <NotificationBar
            content="Successfully Finished all tasks"
            type="success"
            timeout={this._timeout}
            visibility={this._doneList}
          />
        )}
      </span>
    );
  }

  private _words() {
    const audio = (src: string) => {
      return (
        <audio controls ref="audio" controlsList="nodownload">
          <source src={src} type="audio/mp3" />
        </audio>
      );
    };
    const content = () => {
      return (
        <div>
          <v-row class="mx-auto text-center">
            <v-col cols={16}>
              {audio(this._data[this._onItem].questionAudio)}
            </v-col>
          </v-row>
          <v-row class="mx-auto text-center">
            <p>
              Once you have finished listening, click "Start Retelling the
              Story" to retell the story.
            </p>
          </v-row>
        </div>
      );
    };
    return (
      this._frame === STORY_RETELL_STATE.LIST && (
        <Card mod={Module.TTMJ} title={`I can retell stories I hear.`}>
          <div slot="mainContent">{content()}</div>
          <div slot="actions">
            <v-row class="mx-auto text-center">
              <v-col cols={16}>
                <AudioHandler
                  recordID="StoryRetell"
                  label={{
                    start: "Start retelling the story",
                    end: "Stop Recording"
                  }}
                  recordingStartClick={() => {
                    // this._disablePlayback = true;
                  }}
                  disableUserControl
                  disabled={this._doneList || this._disableRecording}
                  callbackOnStop={this.changeRecordingToNext}
                />
              </v-col>
            </v-row>
            <v-row class="mx-auto text-center">
              <v-col>
                <v-btn
                  disabled={!this._enableNext}
                  onclick={() => {
                    this.$router.push({
                      name: "t2mejr-tasklist"
                    });
                  }}
                >
                  Next
                </v-btn>
              </v-col>
            </v-row>
          </div>
        </Card>
      )
    );
  }

  private _description() {
    return (
      this._frame === STORY_RETELL_STATE.DESCRIPTION && (
        <Card mod={Module.TTMJ} title="I can retell stories I hear.">
          <div slot="mainContent">
            <p>
              In this task, you will hear a story. After you finish listening,
              please tell the story again. Try to retell it with as many details
              as you can remember.
            </p>

            <p>Are you ready? Click "Next" to hear the story.</p>
          </div>
          <div slot="actions">
            <v-btn
              disabled={!this._enableDescribeNext}
              onClick={() => {
                this._frame = STORY_RETELL_STATE.LIST;
                this.$forceUpdate();
              }}
            >
              Next
            </v-btn>
          </div>
        </Card>
      )
    );
  }
}

const component = TalkToMeJrStoryRetell;
